import { useCallback, useEffect, useState } from 'react';
import {
  OrderRow,
  PlanEntitlement,
  TrialCapacity,
  listOrders,
  getTrialCapacity,
  approveOrder,
  rejectOrder,
  getPlans,
  extractApiError,
} from '../services/api';
import { PlanEntitlements } from '../components/PlanEntitlements';
import { useAuth } from '../contexts/AuthContext';

/**
 * Operator-Queue fuer Service-Paket-Bestellungen von der Homepage.
 *
 * Backend:
 *   - GET  /api/orders?status=
 *   - POST /api/orders/:id/approve   (-> Tenant-Provisionierung)
 *   - POST /api/orders/:id/reject
 *
 * Entscheidung: Bestellungen sind "Anfrage zur Freigabe" — approve
 * provisioniert erst den Tenant mit den Paket-Diensten, reject schliesst
 * die Anfrage. Kein Auto-Provisioning, kein Payment.
 */

type StatusFilter = 'unverified' | 'pending' | 'approved' | 'rejected' | 'all';
type KindFilter = 'all' | 'order' | 'trial';

const STATUS_LABELS: Record<StatusFilter, string> = {
  unverified: 'E-Mail unbestätigt',
  pending: 'Offen',
  approved: 'Freigegeben',
  rejected: 'Abgelehnt',
  all: 'Alle',
};

const STATUS_BADGE: Record<string, string> = {
  unverified: 'bg-slate-100 text-slate-600',
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-700',
};

const ORDERS_BADGE_POLL_INTERVAL_MS = 30_000;

export function OrdersPage() {
  const { operator } = useAuth();
  const isReadOnly = !!operator?.readOnly;
  const [rows, setRows] = useState<OrderRow[]>([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState<StatusFilter>('pending');
  const [kind, setKind] = useState<KindFilter>('all');
  const [capacity, setCapacity] = useState<TrialCapacity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [approveRow, setApproveRow] = useState<OrderRow | null>(null);
  const [rejectRow, setRejectRow] = useState<OrderRow | null>(null);
  const [plans, setPlans] = useState<PlanEntitlement[]>([]);

  useEffect(() => {
    getPlans().then(setPlans).catch(() => setPlans([]));
  }, []);

  const refresh = useCallback(
    (opts?: { silent?: boolean }) => {
      if (!opts?.silent) setLoading(true);
      getTrialCapacity().then(setCapacity).catch(() => setCapacity(null));
      return listOrders(filter, kind === 'all' ? undefined : kind)
        .then((r) => {
          setRows(r.rows);
          setTotal(r.total);
          setError(null);
        })
        .catch((err) => setError(extractApiError(err)))
        .finally(() => {
          if (!opts?.silent) setLoading(false);
        });
    },
    [filter, kind],
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function handleApprove(row: OrderRow, hostingProvider: string): Promise<void> {
    try {
      await approveOrder(row.id, { hostingProvider });
      setApproveRow(null);
      await refresh({ silent: true });
    } catch (err) {
      setError(extractApiError(err));
    }
  }

  async function handleReject(row: OrderRow, reason: string): Promise<void> {
    try {
      await rejectOrder(row.id, reason);
      setRejectRow(null);
      await refresh({ silent: true });
    } catch (err) {
      setError(extractApiError(err));
    }
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold">Bestellungen (Service-Pakete)</h2>
          <p className="text-sm text-slate-500">
            Anfragen von der Homepage (<code className="text-xs">POST /api/public/orders</code>).
            Approve provisioniert den Tenant mit den Paket-Diensten; Reject schliesst die Anfrage.
          </p>
        </div>
        <div className="flex gap-2">
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as KindFilter)}
            className="px-2 py-1 rounded border"
            aria-label="Art-Filter"
          >
            <option value="all">Alle Arten</option>
            <option value="order">Bestellungen</option>
            <option value="trial">Testzugänge</option>
          </select>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as StatusFilter)}
            className="px-2 py-1 rounded border"
            aria-label="Status-Filter"
          >
            {(['pending', 'unverified', 'approved', 'rejected', 'all'] as StatusFilter[]).map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
        </div>
      </div>

      {capacity && (
        <p className={`text-sm mb-4 ${capacity.available ? 'text-slate-600' : 'text-red-700 font-medium'}`}>
          Laufende Testzugänge: {capacity.active}/{capacity.max}
          {!capacity.available && ' – Obergrenze erreicht, Testzugänge können nicht freigegeben werden (TRIAL_MAX_ACTIVE).'}
        </p>
      )}

      {error && (
        <div role="alert" className="bg-red-50 border border-red-200 text-red-800 rounded px-3 py-2 mb-4 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-slate-500">Lade…</p>
      ) : (
        <table className="w-full bg-white shadow rounded text-sm">
          <thead className="bg-slate-100 text-left uppercase tracking-wide">
            <tr>
              <th className="p-3">Unternehmen</th>
              <th className="p-3">Kennung</th>
              <th className="p-3">Plan</th>
              <th className="p-3">Auswahl</th>
              <th className="p-3">Eingegangen</th>
              <th className="p-3">Status</th>
              <th className="p-3 text-right">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t hover:bg-slate-50 align-top">
                <td className="p-3">
                  <span className="font-medium">{r.companyName}</span>
                  {r.kind === 'trial' && (
                    <span className="badge bg-sky-100 text-sky-800 ml-2">
                      Testzugang{r.emailVerifiedAt ? ' · E-Mail bestätigt' : ''}
                    </span>
                  )}
                  <span className="block text-slate-500 text-xs">{r.contactEmail}</span>
                </td>
                <td className="p-3 font-mono">{r.requestedSlug}</td>
                <td className="p-3"><span className="badge bg-slate-200">{r.plan}</span></td>
                <td className="p-3 text-xs">
                  {[...r.bundles, ...r.modules].length > 0
                    ? [...r.bundles, ...r.modules].join(', ')
                    : <span className="text-slate-400">nur Core</span>}
                </td>
                <td className="p-3 text-slate-500" title={new Date(r.createdAt).toLocaleString()}>
                  {relativeAge(r.createdAt)}
                </td>
                <td className="p-3"><span className={`badge ${STATUS_BADGE[r.status] ?? 'bg-slate-200'}`}>{r.status}</span></td>
                <td className="p-3 text-right space-x-2 whitespace-nowrap">
                  {r.status === 'pending' ? (
                    <>
                      <button
                        onClick={() => setApproveRow(r)}
                        disabled={isReadOnly || (r.kind === 'trial' && capacity !== null && !capacity.available)}
                        className="px-2 py-1 rounded bg-green-600 text-white text-xs hover:bg-green-700 disabled:opacity-40"
                        title={isReadOnly ? 'Read-only-Operator' : 'Freigeben & Tenant provisionieren'}
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => setRejectRow(r)}
                        disabled={isReadOnly}
                        className="px-2 py-1 rounded border border-red-300 text-red-700 text-xs hover:bg-red-50 disabled:opacity-40"
                      >
                        Reject
                      </button>
                    </>
                  ) : (
                    <span className="text-slate-400 text-xs">{r.tenantSlug ? `→ ${r.tenantSlug}` : '—'}</span>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-slate-500">Keine Bestellungen für diesen Filter.</td></tr>
            )}
          </tbody>
        </table>
      )}
      <p className="text-xs text-slate-400 mt-2">{total} Bestellung(en) gesamt im Filter.</p>

      {approveRow && (
        <ApproveModal row={approveRow} plans={plans} onCancel={() => setApproveRow(null)} onConfirm={handleApprove} />
      )}
      {rejectRow && (
        <RejectModal row={rejectRow} onCancel={() => setRejectRow(null)} onConfirm={handleReject} />
      )}
    </div>
  );
}

export function OrdersNavBadge() {
  const [count, setCount] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    function load() {
      listOrders('pending')
        .then((r) => { if (!cancelled) setCount(r.total); })
        .catch(() => { if (!cancelled) setCount(null); });
    }
    load();
    const id = window.setInterval(load, ORDERS_BADGE_POLL_INTERVAL_MS);
    return () => { cancelled = true; window.clearInterval(id); };
  }, []);
  if (count === null || count === 0) return null;
  return (
    <span
      className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-amber-500 text-white text-xs font-semibold"
      aria-label={`${count} offene Bestellungen`}
      title={`${count} offene Bestellungen`}
    >
      {count}
    </span>
  );
}

interface ApproveModalProps {
  row: OrderRow;
  plans: PlanEntitlement[];
  onCancel: () => void;
  onConfirm: (row: OrderRow, hostingProvider: string) => void | Promise<void>;
}

function ApproveModal({ row, plans, onCancel, onConfirm }: ApproveModalProps) {
  const [provider, setProvider] = useState('ionos');
  const [busy, setBusy] = useState(false);
  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    try { await onConfirm(row, provider); } finally { setBusy(false); }
  }
  return (
    <ModalShell onClose={onCancel} ariaLabel="Bestellung freigeben">
      <h3 className="text-lg font-semibold mb-2">Bestellung freigeben?</h3>
      <p className="text-sm text-slate-600 mb-4">
        Provisioniert den Tenant <span className="font-mono">{row.requestedSlug}</span> ({row.plan})
        für <strong>{row.companyName}</strong> mit den ausgewählten Paket-Diensten und startet den
        AWX-Provisioning-Job.
      </p>
      {plans.length > 0 && <PlanEntitlements plan={row.plan} plans={plans} className="mb-4" />}
      <form onSubmit={submit} className="space-y-3">
        <label className="block text-sm">
          <span className="text-slate-700">Hosting-Provider</span>
          <select value={provider} onChange={(e) => setProvider(e.target.value)} className="mt-1 w-full px-2 py-1 border rounded text-sm">
            <option value="ionos">ionos</option>
            <option value="yorizon">yorizon</option>
            <option value="hetzner">hetzner</option>
            <option value="united-cloud">united-cloud</option>
          </select>
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} disabled={busy} className="px-3 py-1 rounded border">Abbrechen</button>
          <button type="submit" disabled={busy} className="px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">
            {busy ? 'Provisioniere…' : 'Freigeben & provisionieren'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

interface RejectModalProps {
  row: OrderRow;
  onCancel: () => void;
  onConfirm: (row: OrderRow, reason: string) => void | Promise<void>;
}

function RejectModal({ row, onCancel, onConfirm }: RejectModalProps) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!reason.trim()) return;
    setBusy(true);
    try { await onConfirm(row, reason.trim()); } finally { setBusy(false); }
  }
  return (
    <ModalShell onClose={onCancel} ariaLabel="Bestellung ablehnen">
      <h3 className="text-lg font-semibold mb-2">Bestellung ablehnen?</h3>
      <p className="text-sm text-slate-600 mb-4">
        Schliesst die Anfrage von <strong>{row.companyName}</strong> ohne Provisionierung.
      </p>
      <form onSubmit={submit} className="space-y-3">
        <label className="block text-sm">
          <span className="text-slate-700">Ablehnungsgrund (erforderlich)</span>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={3} required className="mt-1 w-full px-2 py-1 border rounded text-sm" />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} disabled={busy} className="px-3 py-1 rounded border">Abbrechen</button>
          <button type="submit" disabled={busy || !reason.trim()} className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
            {busy ? 'Lehne ab…' : 'Ablehnen'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function ModalShell({ children, onClose, ariaLabel }: { children: React.ReactNode; onClose: () => void; ariaLabel: string }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded shadow-xl w-full max-w-lg p-5">{children}</div>
    </div>
  );
}

function relativeAge(iso: string | null): string {
  if (!iso) return '—';
  const delta = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(delta)) return iso;
  const m = Math.floor(delta / 60_000);
  if (m < 1) return 'gerade eben';
  if (m < 60) return `vor ${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `vor ${h}h`;
  return `vor ${Math.floor(h / 24)}d`;
}
